/**
 * Typing a number into a box that already holds one.
 *
 * The shop's complaint, exactly: a quantity box sits at `0`, you click it and
 * type `2`, and you get `02`. To enter a plain `2` you first have to select
 * the whole value and overwrite it, every single time, on every row.
 *
 * Two separate things cause that, and both are fixed here.
 *
 * The first is the caret. Clicking a box puts the caret where you clicked —
 * usually after the `0` — so your keystroke is an insertion, not a
 * replacement. The input component answers that by selecting the whole value
 * on focus, so the first key you press replaces it.
 *
 * The second is subtler and is why `02` *stays* on screen. React compares a
 * number input's DOM value to the next value loosely, and `'02' == 2` is true,
 * so it decides nothing changed and leaves the text alone. The state behind it
 * is a correct `2` while the box reads `02` — the two drift apart and only a
 * blur puts them back. Holding the typed text as a string, and tidying it
 * here, keeps what is shown and what is stored the same thing.
 *
 * Everything in this file works on the half-written text a person is in the
 * middle of typing, so `''`, `'-'`, `'.'` and `'1.'` are all legitimate
 * passing states rather than errors to reject.
 */

/**
 * Tidy a half-typed number, keeping it something a person can keep typing.
 *
 * Drops characters that cannot belong, keeps a single leading minus and a
 * single decimal point, and removes the zeros that a leading-zero mistake
 * leaves in front of a real digit — `02` becomes `2`, while `0`, `0.5` and
 * the `0.` on the way to it are all left alone.
 */
export function tidyDraft(raw: string): string {
  const negative = raw.startsWith('-');
  const digitsAndDots = raw.replace(/[^\d.]/g, '');

  // Only the first dot is a decimal point; later ones are stray keystrokes.
  const firstDot = digitsAndDots.indexOf('.');
  const body =
    firstDot === -1
      ? digitsAndDots
      : digitsAndDots.slice(0, firstDot + 1) + digitsAndDots.slice(firstDot + 1).replace(/\./g, '');

  /*
   * Strip zeros that sit in front of another digit. The lookahead is what
   * protects `0.5` and a lone `0`: there, what follows the zeros is a dot or
   * nothing at all, so there is no leading-zero mistake to undo.
   */
  const withoutLeadingZeros = body.replace(/^0+(?=\d)/, '');

  return (negative ? '-' : '') + withoutLeadingZeros;
}

/**
 * The number a half-typed string means, or `null` while it does not mean one
 * yet.
 *
 * `''`, `'-'` and `'.'` are the states you pass through on the way to a
 * number, not numbers themselves. Returning `null` lets the caller leave the
 * stored value untouched rather than writing a spurious `0` into it between
 * two keystrokes.
 */
export function draftValue(draft: string): number | null {
  if (!/\d/.test(draft)) return null;
  const parsed = Number.parseFloat(draft);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * What the box shows when nobody is typing in it.
 *
 * An empty stored value stays empty — some fields use that to mean "no limit"
 * rather than zero, and showing `0` there would say something different.
 */
export function displayValue(value: number | ''): string {
  if (value === '') return '';
  return Number.isFinite(value) ? String(value) : '';
}
